import { CustomerBalanceTransactionTypeEnum, type CustomerTransaction } from '@talos/kyoko';

export const COMPLETE_CUSTOMER_TRANSACTION_DISABLED_TOOLTIP =
  'Transaction cannot be completed by the user who created or authorized it';

export type CustomerTransactionCompleteDualControlEntity = Pick<
  CustomerTransaction,
  'TransactionType' | 'InitiatorUser' | 'DealerUser' | 'TreasuryUser'
>;

export interface IsUserBlockedFromCompletingCustomerTransactionParams {
  entity: CustomerTransactionCompleteDualControlEntity;
  customerRequireWithdrawalApproval: boolean;
  customerRequireDepositApproval: boolean;
  currentUserName?: string;
}

function requiresDualControlForComplete(
  transactionType: CustomerBalanceTransactionTypeEnum,
  customerRequireWithdrawalApproval: boolean,
  customerRequireDepositApproval: boolean
): boolean {
  switch (transactionType) {
    case CustomerBalanceTransactionTypeEnum.Withdrawal:
      return customerRequireWithdrawalApproval;
    case CustomerBalanceTransactionTypeEnum.Deposit:
      return customerRequireDepositApproval;
    case CustomerBalanceTransactionTypeEnum.ForceWithdrawal:
      return false;
    default: {
      const exhaustiveCheck: never = transactionType;
      throw new Error(`Unknown transaction type: ${exhaustiveCheck}`);
    }
  }
}

function getAuthorizerUser(dealerUser?: string, treasuryUser?: string): string | undefined {
  return treasuryUser || dealerUser;
}

export function isUserBlockedFromCompletingCustomerTransaction({
  entity,
  customerRequireWithdrawalApproval,
  customerRequireDepositApproval,
  currentUserName,
}: IsUserBlockedFromCompletingCustomerTransactionParams): boolean {
  if (!currentUserName) {
    return false;
  }

  if (
    !requiresDualControlForComplete(
      entity.TransactionType,
      customerRequireWithdrawalApproval,
      customerRequireDepositApproval
    )
  ) {
    return false;
  }

  const authorizerUser = getAuthorizerUser(entity.DealerUser, entity.TreasuryUser);

  return currentUserName === entity.InitiatorUser || currentUserName === authorizerUser;
}
